import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card } from 'react-bootstrap';

import { Link } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

/* Sacred items that ride the two orbit rings behind the hero heading */
const ORBIT_INNER = ['🪔', '🕉️', '📿'];
const ORBIT_OUTER = ['🔔', '🪷', '🥥', '🛕'];

/* Offerings drifting through the page backdrop */
const BG_DRIFT = [
  { char: '🪔', left: '7%', size: '1.8rem', delay: '0s', duration: '28s' },
  { char: '📿', left: '19%', size: '1.5rem', delay: '7s', duration: '34s' },
  { char: '🕉️', left: '31%', size: '1.6rem', delay: '13s', duration: '30s' },
  { char: '🪷', left: '44%', size: '1.7rem', delay: '4s', duration: '37s' },
  { char: '🔔', left: '57%', size: '1.4rem', delay: '19s', duration: '31s' },
  { char: '🛕', left: '69%', size: '1.8rem', delay: '10s', duration: '35s' },
  { char: '🥥', left: '81%', size: '1.5rem', delay: '23s', duration: '29s' },
  { char: '🪔', left: '93%', size: '1.4rem', delay: '16s', duration: '38s' },
  { char: '🪷', left: '38%', size: '1.3rem', delay: '26s', duration: '33s' },
  { char: '🕉️', left: '63%', size: '1.3rem', delay: '31s', duration: '36s' }
];

/* Deterministic scatter so the stars don't reshuffle between renders */
const BG_STARS = Array.from({ length: 22 }, (_, i) => ({
  left: `${((i * 41) % 96) + 2}%`,
  top: `${((i * 59) % 90) + 4}%`,
  size: `${2 + (i % 3)}px`,
  delay: `${(i * 0.47).toFixed(2)}s`,
  duration: `${(2.8 + (i % 6) * 0.7).toFixed(2)}s`
}));

/* Fixed, full-viewport animated backdrop for this page. Sits at z-index -1 so
   it paints behind the content and unmounts with the page. The ripple rings
   echo the orbit rings in the hero. All motion is transform/opacity only. */
function CategoriesBackdrop() {
  return (
    <div className="cbg" aria-hidden="true">
      <span className="cbg-veil" />

      {/* Blobs that morph as well as drift */}
      <span className="cbg-blob cbg-blob-1" />
      <span className="cbg-blob cbg-blob-2" />
      <span className="cbg-blob cbg-blob-3" />

      {/* Ripple rings expanding from two points, like a temple pond */}
      <span className="cbg-ripples cbg-ripples-a">
        {[0, 1, 2].map((n) => (
          <span className="cbg-ring" key={`a${n}`} />
        ))}
      </span>
      <span className="cbg-ripples cbg-ripples-b">
        {[0, 1, 2].map((n) => (
          <span className="cbg-ring" key={`b${n}`} />
        ))}
      </span>

      {BG_STARS.map((star, i) => (
        <span
          key={`star-${i}`}
          className="cbg-star"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            animationDelay: star.delay,
            animationDuration: star.duration
          }}
        />
      ))}

      {BG_DRIFT.map((item, i) => (
        <span
          key={`drift-${i}`}
          /* every other one is dropped on phones — see .cbg-drift-extra */
          className={`cbg-drift${i % 2 ? ' cbg-drift-extra' : ''}`}
          style={{
            left: item.left,
            fontSize: item.size,
            animationDelay: item.delay,
            animationDuration: item.duration
          }}
        >
          {item.char}
        </span>
      ))}
    </div>
  );
}

function CategoriesPage() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(
          `${API}/categories`
        );

        setCategories(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCategories();
  }, []);

  /* Repeat the names enough times to fill the ticker, always an even number of
     copies so the -50% scroll loops seamlessly. Each copy keeps the category's
     original index so the alternating accent colour matches across copies. */
  const tickerItems =
    categories.length > 0
      ? Array.from({ length: categories.length >= 6 ? 2 : 4 }).flatMap(() =>
          categories.map((category, idx) => ({ category, idx }))
        )
      : [];

  /* Tilt the hero towards the pointer — written straight to the node so a
     mousemove never triggers a React render. */
  const tiltHero = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;

    e.currentTarget.style.setProperty('--ry', `${px * 9}deg`);
    e.currentTarget.style.setProperty('--rx', `${-py * 7}deg`);
  };

  const resetTilt = (e) => {
    e.currentTarget.style.setProperty('--ry', '0deg');
    e.currentTarget.style.setProperty('--rx', '0deg');
  };

  /* Cards tilt towards the pointer and carry a spotlight under it. Same
     write-straight-to-the-node trick as the hero — no render per mousemove. */
  const trackCard = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;

    el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
    el.style.setProperty('--cry', `${((px - 0.5) * 11).toFixed(2)}deg`);
    el.style.setProperty('--crx', `${(-(py - 0.5) * 9).toFixed(2)}deg`);

    /* unitless -0.5..0.5, used to parallax the caption against the tilt */
    el.style.setProperty('--px', (px - 0.5).toFixed(3));
    el.style.setProperty('--py', (py - 0.5).toFixed(3));
  };

  const resetCard = (e) => {
    const el = e.currentTarget;

    el.style.setProperty('--cry', '0deg');
    el.style.setProperty('--crx', '0deg');
    el.style.setProperty('--px', '0');
    el.style.setProperty('--py', '0');
  };

  return (
    <>
      <CategoriesBackdrop />

      <CustomNavbar />

      <div className="cat-wrap">
        <Container>
          {/* Header Card */}

          <Card
            className="border-0 shadow-lg text-center mb-5 cat-hero"
            onMouseMove={tiltHero}
            onMouseLeave={resetTilt}
          >
            <span className="cat-hero-blob cat-hero-blob-1" />
            <span className="cat-hero-blob cat-hero-blob-2" />

            {/* Two counter-rotating rings of category emoji */}
            <span className="cat-orbit cat-orbit-inner" aria-hidden="true">
              {ORBIT_INNER.map((emoji) => (
                <span className="cat-orbit-arm" key={emoji}>
                  <span className="cat-orbit-item">{emoji}</span>
                </span>
              ))}
            </span>

            <span className="cat-orbit cat-orbit-outer" aria-hidden="true">
              {ORBIT_OUTER.map((emoji) => (
                <span className="cat-orbit-arm" key={emoji}>
                  <span className="cat-orbit-item">{emoji}</span>
                </span>
              ))}
            </span>

            <div className="position-relative py-2 cat-hero-content">
              <span className="cat-hero-pill mb-3 d-inline-block">
                🛍️ Browse our collections
              </span>

              <h1 className="fw-bold cat-hero-title">Shop By Categories</h1>

              <p className="mb-0 cat-hero-text">
                Explore collections and discover products by category.
              </p>

              {/* Live ticker of the real category names — each name is a
                  clickable pill straight into that category */}
              {tickerItems.length > 0 && (
                <div className="cat-ticker">
                  <div className="cat-ticker-track">
                    {tickerItems.map(({ category, idx }, i) => (
                      <Link
                        to={`/category/${category.name}`}
                        className={`cat-ticker-item${
                          idx % 2 ? ' cat-ticker-item-alt' : ''
                        }`}
                        style={{ '--pill-delay': `${(idx % 5) * -0.9}s` }}
                        key={`${category._id}-${i}`}
                      >
                        <span className="cat-ticker-dot" />
                        <span className="cat-ticker-label">
                          {category.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {categories.length === 0 ? (
            <Row className="g-4">
              {[1, 2, 3].map((n) => (
                <Col xl={3} lg={4} md={6} sm={6} xs={12} key={n}>
                  <Card
                    className="border-0 shadow-sm cat-skeleton"
                    style={{ borderRadius: '20px' }}
                  >
                    <div className="cat-skeleton-box" />
                    <Card.Body>
                      <div className="cat-skeleton-line mx-auto" />
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Row className="g-4">
              {categories.map((category, index) => (
                <Col
                  xl={3}
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  key={category._id}
                  className="cat-card-in"
                  /* --i drives both the entrance stagger and the idle float
                     offset; capped so later cards don't wait seconds */
                  style={{ '--i': Math.min(index, 11) }}
                >
                  <Link
                    to={`/category/${category.name}`}
                    className="text-decoration-none cat-card-link"
                  >
                    <Card
                      className="border-0 shadow-sm h-100 cat-card"
                      onMouseMove={trackCard}
                      onMouseLeave={resetCard}
                    >
                      <div className="cat-img-wrap">
                        <Card.Img
                          variant="top"
                          src={category.image}
                          alt={category.name}
                          className="cat-img"
                        />
                        <div className="cat-overlay" />

                        {/* Spotlight that follows the pointer */}
                        <span className="cat-spot" aria-hidden="true" />
                        {/* Warm colour rising from the bottom edge on hover */}
                        <span className="cat-wash" aria-hidden="true" />
                        {/* Sparkle popping into the top corner on hover */}
                        <span className="cat-spark" aria-hidden="true">
                          ✦
                        </span>

                        <div className="cat-content">
                          <h3 className="fw-bold mb-2 cat-name">
                            {category.name}
                          </h3>
                          <span className="cat-explore">
                            <span className="cat-explore-label">
                              Explore Category
                            </span>
                            <span className="cat-explore-arrow">→</span>
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>

      <style>{categoriesStyles}</style>
    </>
  );
}

const categoriesStyles = `
  /* Transparent — the fixed .cbg layer below paints the page */
  .cat-wrap {
    background: transparent;
    position: relative;
    min-height: 100vh;
    padding: clamp(28px, 5vw, 50px) 0;
  }

  /* ===== Animated page backdrop ===== */
  /* Same base palette as the Products page so the two listings read as a set */
  .cbg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background:
      radial-gradient(760px 340px at 8% -8%, rgba(124,58,237,0.14), transparent 70%),
      radial-gradient(760px 340px at 92% 108%, rgba(234,88,12,0.16), transparent 70%),
      linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
  }

  /* Second palette crossfading over the base — opacity/transform only, so it
     composites on the GPU rather than repainting the gradient every frame */
  .cbg-veil {
    position: absolute;
    inset: -20%;
    background:
      radial-gradient(48% 52% at 22% 26%, rgba(251,191,36,0.32), transparent 65%),
      radial-gradient(44% 48% at 80% 32%, rgba(168,85,247,0.26), transparent 65%),
      radial-gradient(54% 58% at 52% 86%, rgba(234,88,12,0.28), transparent 65%);
    opacity: 0.4;
    animation: cbgVeil 24s ease-in-out infinite alternate;
    will-change: transform, opacity;
  }
  @keyframes cbgVeil {
    0%   { opacity: 0.28; transform: translate3d(-2%, 2%, 0) scale(1); }
    100% { opacity: 0.66; transform: translate3d(3%, -2%, 0) scale(1.16); }
  }

  /* Blobs that morph their outline while they drift */
  .cbg-blob {
    position: absolute;
    filter: blur(64px);
    pointer-events: none;
    will-change: transform, border-radius;
  }
  .cbg-blob-1 {
    width: 420px; height: 420px;
    top: -110px; left: -90px;
    background: #ea580c;
    opacity: 0.34;
    animation: cbgMorph 19s ease-in-out infinite;
  }
  .cbg-blob-2 {
    width: 380px; height: 380px;
    bottom: -130px; right: -80px;
    background: #a855f7;
    opacity: 0.28;
    animation: cbgMorph 25s ease-in-out infinite reverse;
  }
  .cbg-blob-3 {
    width: 320px; height: 320px;
    top: 44%; right: 18%;
    background: #f59e0b;
    opacity: 0.26;
    animation: cbgMorph 31s ease-in-out infinite;
  }
  @keyframes cbgMorph {
    0%, 100% { border-radius: 46% 54% 62% 38% / 44% 42% 58% 56%; transform: translate3d(0, 0, 0) scale(1); }
    33%      { border-radius: 62% 38% 41% 59% / 57% 63% 37% 43%; transform: translate3d(42px, 34px, 0) scale(1.16); }
    66%      { border-radius: 38% 62% 57% 43% / 39% 46% 54% 61%; transform: translate3d(-36px, 44px, 0) scale(0.9); }
  }

  /* Ripple rings — an echo of the hero's orbit rings */
  .cbg-ripples { position: absolute; width: 0; height: 0; }
  .cbg-ripples-a { top: 22%; left: 14%; }
  .cbg-ripples-b { top: 74%; left: 82%; }
  .cbg-ring {
    position: absolute;
    top: 0; left: 0;
    width: 260px; height: 260px;
    margin: -130px 0 0 -130px;
    border-radius: 50%;
    border: 1px solid rgba(245, 158, 11, 0.45);
    opacity: 0;
    animation: cbgRipple 11s ease-out infinite;
    will-change: transform, opacity;
  }
  .cbg-ring:nth-child(2) { animation-delay: -3.7s; }
  .cbg-ring:nth-child(3) { animation-delay: -7.4s; }
  .cbg-ripples-b .cbg-ring {
    border-color: rgba(168, 85, 247, 0.42);
    animation-duration: 14s;
  }
  @keyframes cbgRipple {
    0%   { opacity: 0;    transform: scale(0.2); }
    14%  { opacity: 0.55; }
    100% { opacity: 0;    transform: scale(3.6); }
  }

  /* Twinkling stars scattered across the viewport */
  .cbg-star {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 35%, #fffbeb, #fbbf24 60%, rgba(245,158,11,0));
    box-shadow: 0 0 9px rgba(251, 191, 36, 0.85);
    opacity: 0;
    animation-name: cbgTwinkle;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
  }
  @keyframes cbgTwinkle {
    0%, 100% { opacity: 0;   transform: scale(0.4); }
    50%      { opacity: 0.9; transform: scale(1.4); }
  }

  /* Offerings drifting upward off the bottom edge */
  .cbg-drift {
    position: absolute;
    bottom: -70px;
    line-height: 1;
    opacity: 0;
    filter: drop-shadow(0 6px 14px rgba(180, 83, 9, 0.22));
    animation-name: cbgRise;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }
  @keyframes cbgRise {
    0%   { opacity: 0;   transform: translate3d(0, 0, 0) rotate(0deg) scale(0.85); }
    10%  { opacity: 0.5; }
    50%  { transform: translate3d(-30px, -55vh, 0) rotate(-13deg) scale(1); }
    88%  { opacity: 0.38; }
    100% { opacity: 0;   transform: translate3d(28px, -115vh, 0) rotate(11deg) scale(1.12); }
  }

  @media (max-width: 767.98px) {
    /* Fewer moving parts on phones */
    .cbg-blob-3, .cbg-ripples-b, .cbg-drift-extra { display: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    .cbg-veil,
    .cbg-blob,
    .cbg-ring,
    .cbg-star,
    .cbg-drift { animation: none !important; }
    .cbg-ring, .cbg-drift { display: none; }
    .cbg-star { opacity: 0.5; }
  }

  /* ===== Shop By Categories hero ===== */
  .cat-hero {
    border-radius: 25px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #042f2e 0%, #0f172a 40%, #7c2d12 78%, #ea580c 100%);
    padding: clamp(34px, 5vw, 58px);
    position: relative;
    transform: perspective(1100px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
    transform-style: preserve-3d;
    transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease;
  }
  .cat-hero:hover {
    box-shadow: 0 34px 70px rgba(15, 23, 42, 0.45) !important;
  }
  .cat-hero-content {
    z-index: 4;
    transform: translateZ(45px);
  }

  /* Blurred shapes that slowly morph rather than sit still */
  .cat-hero-blob {
    position: absolute;
    filter: blur(58px);
    opacity: 0.5;
    pointer-events: none;
    z-index: 0;
  }
  .cat-hero-blob-1 {
    width: 250px; height: 250px;
    background: #f59e0b;
    top: -100px; left: 8%;
    border-radius: 46% 54% 62% 38% / 44% 42% 58% 56%;
    animation: catMorph 16s ease-in-out infinite;
  }
  .cat-hero-blob-2 {
    width: 230px; height: 230px;
    background: #2dd4bf;
    bottom: -100px; right: 6%;
    opacity: 0.35;
    border-radius: 58% 42% 39% 61% / 52% 61% 39% 48%;
    animation: catMorph 21s ease-in-out infinite reverse;
  }

  /* Orbit rings — arms carry the emoji, each emoji counter-spins so it
     stays upright while travelling around the circle. */
  .cat-orbit {
    position: absolute;
    top: 50%; left: 50%;
    border: 1px dashed rgba(255, 255, 255, 0.14);
    border-radius: 50%;
    pointer-events: none;
    z-index: 1;
  }
  .cat-orbit-inner {
    width: 460px; height: 460px;
    margin: -230px 0 0 -230px;
  }
  .cat-orbit-outer {
    width: 680px; height: 680px;
    margin: -340px 0 0 -340px;
    border-color: rgba(255, 255, 255, 0.09);
  }
  .cat-orbit-arm {
    position: absolute;
    inset: 0;
    animation: catSpin 30s linear infinite;
  }
  .cat-orbit-item {
    position: absolute;
    top: -19px; left: 50%;
    margin-left: -19px;
    width: 38px; height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.28);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
    backdrop-filter: blur(5px);
    animation: catSpin 30s linear infinite reverse;
  }
  /* Stagger the arms around each ring via negative delays */
  .cat-orbit-inner .cat-orbit-arm:nth-child(1),
  .cat-orbit-inner .cat-orbit-arm:nth-child(1) .cat-orbit-item { animation-delay: 0s; }
  .cat-orbit-inner .cat-orbit-arm:nth-child(2),
  .cat-orbit-inner .cat-orbit-arm:nth-child(2) .cat-orbit-item { animation-delay: -10s; }
  .cat-orbit-inner .cat-orbit-arm:nth-child(3),
  .cat-orbit-inner .cat-orbit-arm:nth-child(3) .cat-orbit-item { animation-delay: -20s; }

  .cat-orbit-outer .cat-orbit-arm {
    animation-duration: 46s;
    animation-direction: reverse;
  }
  .cat-orbit-outer .cat-orbit-item {
    animation-duration: 46s;
    animation-direction: normal;
  }
  .cat-orbit-outer .cat-orbit-arm:nth-child(1),
  .cat-orbit-outer .cat-orbit-arm:nth-child(1) .cat-orbit-item { animation-delay: 0s; }
  .cat-orbit-outer .cat-orbit-arm:nth-child(2),
  .cat-orbit-outer .cat-orbit-arm:nth-child(2) .cat-orbit-item { animation-delay: -11.5s; }
  .cat-orbit-outer .cat-orbit-arm:nth-child(3),
  .cat-orbit-outer .cat-orbit-arm:nth-child(3) .cat-orbit-item { animation-delay: -23s; }
  .cat-orbit-outer .cat-orbit-arm:nth-child(4),
  .cat-orbit-outer .cat-orbit-arm:nth-child(4) .cat-orbit-item { animation-delay: -34.5s; }

  .cat-hero-pill {
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 50px;
    padding: 6px 16px;
    font-size: 0.85rem;
    font-weight: 600;
    backdrop-filter: blur(6px);
  }
  /* Light flows through the letters left to right, forever */
  .cat-hero-title {
    font-size: clamp(1.8rem, 5vw, 3rem);
    letter-spacing: -0.5px;
    background: linear-gradient(100deg, #ffffff 0%, #99f6e4 18%, #fde68a 34%, #ffffff 50%, #99f6e4 68%, #fde68a 84%, #ffffff 100%);
    background-size: 260% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: catFlow 9s linear infinite;
  }
  .cat-hero-text {
    opacity: 0.82;
    font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  }

  /* Endless ticker of the real category names, rendered as glass pills */
  .cat-ticker {
    position: relative;
    margin-top: 26px;
    overflow: hidden;
    padding: 20px 0 6px;
    mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent);
  }
  /* Hairline that fades out towards both edges, replacing the hard borders */
  .cat-ticker::before {
    content: '';
    position: absolute;
    top: 0; left: 6%; right: 6%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.34), transparent);
  }
  .cat-ticker-track {
    display: flex;
    align-items: center;
    width: max-content;
    animation: catTicker 30s linear infinite;
  }
  .cat-ticker:hover .cat-ticker-track {
    animation-play-state: paused;
  }
  .cat-ticker-item {
    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    margin: 0 7px;
    padding: 9px 20px;
    border-radius: 50px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    white-space: nowrap;
    text-decoration: none;
    color: rgba(255, 247, 237, 0.88);
    background: linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04));
    border: 1px solid rgba(255,255,255,0.2);
    backdrop-filter: blur(6px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease, border-color 0.35s ease, color 0.35s ease;
  }
  /* Light sweeps across every pill, staggered so they don't flash in unison */
  .cat-ticker-item::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.3) 50%, transparent 62%);
    background-size: 260% 100%;
    animation: catSheen 4.5s linear infinite;
    animation-delay: var(--pill-delay, 0s);
  }
  .cat-ticker-item:hover {
    transform: translateY(-4px) scale(1.06);
    color: #fff;
    border-color: rgba(251, 191, 36, 0.75);
    box-shadow: 0 14px 28px rgba(251, 191, 36, 0.3);
  }
  .cat-ticker-item-alt:hover {
    border-color: rgba(94, 234, 212, 0.75);
    box-shadow: 0 14px 28px rgba(45, 212, 191, 0.3);
  }
  .cat-ticker-label {
    position: relative;
    z-index: 1;
  }
  .cat-ticker-dot {
    width: 7px;
    height: 7px;
    flex: none;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 0 10px rgba(251, 191, 36, 0.95);
    animation: catPulse 2.4s ease-in-out infinite;
    animation-delay: var(--pill-delay, 0s);
  }
  .cat-ticker-item-alt .cat-ticker-dot {
    background: #5eead4;
    box-shadow: 0 0 10px rgba(94, 234, 212, 0.95);
  }

  @keyframes catSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes catMorph {
    0%, 100% { border-radius: 46% 54% 62% 38% / 44% 42% 58% 56%; transform: translate(0, 0) scale(1); }
    33%  { border-radius: 62% 38% 41% 59% / 57% 63% 37% 43%; transform: translate(18px, 12px) scale(1.12); }
    66%  { border-radius: 38% 62% 57% 43% / 39% 46% 54% 61%; transform: translate(-14px, 18px) scale(0.94); }
  }
  @keyframes catFlow {
    from { background-position: 0% 50%; }
    to   { background-position: 260% 50%; }
  }
  @keyframes catTicker {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @keyframes catSheen {
    from { background-position: 160% 50%; }
    to   { background-position: -60% 50%; }
  }
  @keyframes catPulse {
    0%, 100% { transform: scale(1);   opacity: 1; }
    50%      { transform: scale(1.5); opacity: 0.65; }
  }

  @media (max-width: 767.98px) {
    .cat-orbit-inner { width: 340px; height: 340px; margin: -170px 0 0 -170px; }
    .cat-orbit-outer { width: 500px; height: 500px; margin: -250px 0 0 -250px; }
    .cat-orbit-item { width: 32px; height: 32px; top: -16px; margin-left: -16px; font-size: 0.9rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .cat-hero { transform: none; transition: none; }
    .cat-hero-blob,
    .cat-orbit-arm,
    .cat-orbit-item,
    .cat-hero-title,
    .cat-ticker-track,
    .cat-ticker-item::after,
    .cat-ticker-dot { animation: none; }
    .cat-ticker-item:hover { transform: none; }
    .cat-hero-title { color: #fff7ed; -webkit-text-fill-color: #fff7ed; }
  }

  /* ===== Category cards ===== */

  /* Cards rise into place on mount, staggered by --i */
  .cat-card-in {
    animation: catCardIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: calc(var(--i, 0) * 75ms);
  }
  @keyframes catCardIn {
    from { opacity: 0; transform: translateY(30px) scale(0.94); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* The link is a separate layer from the card so the idle float and the
     hover tilt can each own a transform without fighting each other */
  .cat-card-link {
    position: relative;
    display: block;
    isolation: isolate;
    animation: catFloat 6.5s ease-in-out infinite;
    animation-delay: calc(var(--i, 0) * -0.85s);
  }
  @keyframes catFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-7px); }
  }


  .cat-card {
    border-radius: 20px;
    overflow: hidden;
    /* rotate vars are written by the pointer handler; lift/scale by :hover */
    transform:
      perspective(900px)
      rotateX(var(--crx, 0deg))
      rotateY(var(--cry, 0deg))
      translateY(var(--clift, 0px))
      scale(var(--cscale, 1));
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;
  }
  /* Ring hugs the card edge instead of a blurred halo, so nothing bleeds
     into the neighbouring cards */
  .cat-card:hover {
    --clift: -10px;
    --cscale: 1.02;
    box-shadow:
      0 0 0 2px rgba(251, 191, 36, 0.85),
      0 22px 44px rgba(194, 65, 12, 0.3) !important;
  }
  .cat-img-wrap {
    position: relative;
    overflow: hidden;
    border-radius: 20px;
  }
  .cat-img {
    height: clamp(200px, 26vw, 260px);
    width: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), filter 0.4s ease;
    border-radius: 20px;
  }
  .cat-card:hover .cat-img {
    transform: scale(1.12) rotate(1.5deg);
    filter: saturate(1.15) contrast(1.05);
  }
  .cat-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.15) 55%, rgba(15,23,42,0) 100%);
    transition: background 0.4s ease;
  }
  .cat-card:hover .cat-overlay {
    background: linear-gradient(to top,
      rgba(67,20,7,0.9) 0%, rgba(88,28,135,0.3) 58%, rgba(15,23,42,0.05) 100%);
  }

  /* Warm spotlight tracking the pointer (--mx / --my set in JS) */
  .cat-spot {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(150px circle at var(--mx, 50%) var(--my, 50%),
      rgba(251, 191, 36, 0.22), rgba(251, 191, 36, 0.06) 45%, transparent 70%);
    opacity: 0;
    transition: opacity 0.35s ease;
  }
  .cat-card:hover .cat-spot { opacity: 1; }

  /* Warm colour rises from the bottom edge — clipped by .cat-img-wrap, so it
     can never spill onto a neighbouring card */
  .cat-wash {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(to top,
      rgba(234, 88, 12, 0.6) 0%, rgba(168, 85, 247, 0.28) 42%, transparent 72%);
    transform: translateY(100%);
    transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .cat-card:hover .cat-wash { transform: translateY(0); }

  /* Sparkle spins into the top corner */
  .cat-spark {
    position: absolute;
    top: 12px;
    right: 14px;
    font-size: 1.1rem;
    line-height: 1;
    color: #fde68a;
    pointer-events: none;
    text-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    transform: scale(0.3) rotate(-120deg);
    transition: opacity 0.35s ease,
      transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .cat-card:hover .cat-spark {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    animation: catSparkTwinkle 2.2s ease-in-out 0.5s infinite;
  }
  @keyframes catSparkTwinkle {
    0%, 100% { opacity: 1;    text-shadow: 0 0 12px rgba(251, 191, 36, 0.9); }
    50%      { opacity: 0.55; text-shadow: 0 0 22px rgba(251, 191, 36, 1); }
  }

  /* Caption slides against the tilt, so it reads as floating above the image */
  .cat-content {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    padding: 18px 20px;
    color: #fff;
    text-align: center;
    transform: translate3d(
      calc(var(--px, 0) * -16px),
      calc(var(--py, 0) * -13px),
      0
    );
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .cat-name {
    position: relative;
    display: inline-block;
    font-size: clamp(1.2rem, 3vw, 1.5rem);
    text-shadow: 0 2px 8px rgba(0,0,0,0.4);
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .cat-card:hover .cat-name { transform: translateY(-3px); }
  /* Gold rule that grows out from the centre under the name */
  .cat-name::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: -5px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, #fbbf24, #f0abfc, transparent);
    transform: scaleX(0);
    transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .cat-card:hover .cat-name::after { transform: scaleX(1); }

  .cat-explore {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: linear-gradient(120deg, #fbbf24, #f59e0b 55%, #fb923c);
    background-size: 200% 100%;
    color: #111827;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 7px 18px;
    border-radius: 50px;
    box-shadow: 0 8px 20px rgba(245, 158, 11, 0.35);
    transform: translateY(10px);
    opacity: 0;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;
  }
  .cat-card:hover .cat-explore {
    transform: translateY(0);
    opacity: 1;
    animation: catExploreFlow 3s ease-in-out 0.35s infinite alternate;
  }
  @keyframes catExploreFlow {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .cat-explore-arrow {
    display: inline-block;
    transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .cat-card:hover .cat-explore-arrow { transform: translateX(5px); }

  @media (prefers-reduced-motion: reduce) {
    .cat-card-in,
    .cat-card-link,
    .cat-card:hover .cat-spark,
    .cat-card:hover .cat-explore { animation: none !important; }
    .cat-card { transform: none; transition: box-shadow 0.3s ease; }
    .cat-card:hover .cat-img { transform: none; }
    .cat-card:hover .cat-name { transform: none; }
    .cat-content { transform: none; }
    .cat-wash { transition: none; }
  }

  /* Skeleton loaders */
  .cat-skeleton-box {
    height: clamp(200px, 26vw, 260px);
    border-radius: 20px 20px 0 0;
  }
  .cat-skeleton-line {
    height: 18px;
    width: 60%;
    border-radius: 6px;
  }
  .cat-skeleton-box, .cat-skeleton-line {
    background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%);
    background-size: 400% 100%;
    animation: catShimmer 1.4s ease infinite;
  }
  @keyframes catShimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

export default CategoriesPage;
